<template lang="pug">
div
  aiq-dialog.modal-editor(:title="getTitle",
        v-model="modalShown"
        :show-close="false"
        :close-on-click-modal="true"
        :close-on-press-escape="true"
        :before-close="handleBeforeClose"
        @closed="closeEditor"
        destroy-on-close
        v-on="$attrs")

    .field-container.is-required
      label {{ $t(`${baseTranslationPath}.fields.content.title`) }}
      .el-input
        .custom-rich-text-container
          rich-text(@update="onUpdateRichTextEditor"
                :inChat="false"
                :showImageUpload="true"
                :place-holder="$t(`${baseTranslationPath}.fields.content.placeholder`)")
    aiq-tooltip(v-if="showToggle" effect="dark", placement="left", :content="$t(`${baseTranslationPath}.fields.pin.tooltip`)", :show-after="500")
      .field-container.switch-row
        .label-container
          label {{ $t(`${baseTranslationPath}.fields.pin.title`) }}
          fa-icon.info-icon(:icon="['fas', 'circle-question']")
        .switch-container
          aiq-switch.pin-control(v-model="accordionItem[this.toggleProperty]")

    slot(name="extra-fields" :accordionItem="accordionItem" :isBusy="isBusy")

    template(v-slot:footer)
      p.required-notice * Required
      span.spinner
        font-awesome-icon(v-if="isBusy"
              size="lg"
              icon="spinner"
              spin)
      aiq-button.button-delete(v-if="!isAddMode"
            type="danger"
            :plain="true"
            :disabled="isBusy"
            @click="deleteRecord") {{ $t('global.delete') }}
      aiq-button(@click="closeEditor" :disabled="isBusy") {{ $t('global.cancel') }}
      aiq-button(:disabled="!isSubmittable || isBusy"
                type="primary"
                @click="saveRecord") {{ $t('global.save') }}
</template>

<script>

import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import mitt from 'mitt';
import get from 'lodash/get';
import RichText from '@/components/RichText/RichText.vue';
import { confirmBeforeDialogClose } from '@/libs';

export default {
  name: 'accordion-item-editor',
  components: {
    RichText,
  },
  provide() {
    return {
      richTextEventEmitter: this.richTextEventEmitter,
    };
  },
  setup() {
    return { v$: useVuelidate() };
  },
  validations() {
    return {
      accordionItem: {
        content: { required, $autoDirty: true },
        ...(this.enableExpireDate ? { expire_date: { $autoDirty: true } } : {}),
        ...(this.enableDisplayFrequency ? { display_frequency: { $autoDirty: true } } : {}),
      },
    };
  },
  data() {
    return {
      isBusy: false,
      modalShown: false,
      accordionItem: {},
      richTextEventEmitter: mitt(),
      defaultItem: {
        content: null,
        [this.toggleProperty]: false,
      },
    };
  },
  props: {
    baseTranslationPath: {
      type: String,
      required: true,
    },
    item: {
      type: Object,
    },
    isVisible: {
      type: Boolean,
      required: true,
    },
    toggleProperty: {
      type: String,
      required: true,
    },
    showToggle: {
      type: Boolean,
      default: true,
    },
    enableExpireDate: {
      type: Boolean,
      default: false,
    },
    enableDisplayFrequency: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['close', 'save', 'remove'],

  computed: {
    getTitle() {
      const editorMode = this.item?.id ? 'edit' : 'add';
      return this.$t(`${this.baseTranslationPath}.editor.${editorMode}`);
    },
    isSubmittable() {
      return !this.v$.$invalid;
    },
    isAddMode() {
      return !get(this.item, 'id');
    },
    isRecordDirty() {
      if (this.v$) {
        return this.v$.$anyDirty;
      }
      return true;
    },
  },

  watch: {
    isVisible: {
      handler(newVal) {
        this.modalShown = newVal;
        this.isBusy = false;

        if (this.modalShown) {
          this.initAccordionItem(this.item);
          this.$nextTick(() => this.richTextEventEmitter.emit('setText', this.accordionItem.content));
        }
      },
      immediate: true,
    },
  },

  methods: {
    closeEditor() {
      this.$emit('close');
    },
    saveRecord() {
      this.isBusy = true;
      this.$emit('save', this.accordionItem);
    },
    deleteRecord() {
      this.isBusy = true;
      this.$emit('remove', this.accordionItem);
    },
    onUpdateRichTextEditor(msgData) {
      this.accordionItem.content = msgData.text === '' ? '' : msgData.html;
    },
    initAccordionItem(newVal = null) {
      this.accordionItem = newVal ? {...newVal } : {...this.defaultItem};
    },
    handleBeforeClose() {
      if (!this.isRecordDirty) {
        this.closeEditor();
        return;
      }

      confirmBeforeDialogClose(this, this.closeEditor);
    },
  },
};
</script>

<style lang="scss" scoped>
.switch-row {
  display: flex;
  align-items: center;
  width: 100%;

  .label-container {
    flex: 1;

    .info-icon {
      margin-left: 10px;
      color: #6B6D70;
    }
  }
  .switch-container {
    flex: 2;

    .pin-control {
      text-align: center;
    }
  }
}

:deep(.modal-editor) {
  &.el-dialog {
    overflow: visible !important;
  }
  footer {
    .spinner {
      margin-right: 10px;
    }
  }
}

</style>
